"use client";

import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const filters = [{
  id: "all",
  label: "ALL"
}, {
  id: "security",
  label: "SECURITY"
}, {
  id: "privacy",
  label: "PRIVACY"
}, {
  id: "speed",
  label: "SPEED"
}];
const fallbackFilms = [{
  id: "f1",
  title: "VPN BASICS",
  year: "2024",
  country: "GLOBAL",
  runtime: "5 MIN",
  director: "VPNGENIX",
  commentary: "Master the fundamentals of secure browsing and data encryption.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_basics.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_basics.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_basics.png-wvc-sizes",
  nextScreening: "UPDATED — MAR 14",
  featured: true,
  category: "SECURITY",
  link: "/VpnGenixPage"
}, {
  id: "f2",
  title: "DATA SHIELD",
  year: "2024",
  country: "GLOBAL",
  runtime: "8 MIN",
  director: "VPNGENIX",
  commentary: "Protect your digital identity from trackers and malicious actors.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/data_shield.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/data_shield.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/data_shield.png-wvc-sizes",
  nextScreening: "UPDATED — MAR 15",
  featured: false,
  category: "SECURITY",
  link: "/CybersecurityCyberGenixPage"
}, {
  id: "f3",
  title: "SAAS TOOLS",
  year: "2024",
  country: "GLOBAL",
  runtime: "12 MIN",
  director: "VPNGENIX",
  commentary: "Top-tier software solutions for your business productivity needs.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/saas_tools.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/saas_tools.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/saas_tools.png-wvc-sizes",
  nextScreening: "UPDATED — MAR 16",
  featured: false,
  category: "PRIVACY",
  link: "/SaasToolsSaasGenixPage"
}, {
  id: "f4",
  title: "AI INSIGHTS",
  year: "2024",
  country: "GLOBAL",
  runtime: "10 MIN",
  director: "VPNGENIX",
  commentary: "Leveraging artificial intelligence for smarter, faster workflows.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/ai_insights.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/ai_insights.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/ai_insights.png-wvc-sizes",
  nextScreening: "UPDATED — MAR 17",
  featured: false,
  category: "PRIVACY",
  link: "/AiToolsAIGenixPage"
}, {
  id: "f5",
  title: "STREAMING",
  year: "2024",
  country: "GLOBAL",
  runtime: "6 MIN",
  director: "VPNGENIX",
  commentary: "Unlock global content with our high-speed streaming protocols.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/streaming_vpn.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/streaming_vpn.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/streaming_vpn.png-wvc-sizes",
  nextScreening: "UPDATED — MAR 18",
  featured: false,
  category: "SPEED",
  link: "/StreamingVPNsPage"
}, {
  id: "f6",
  title: "MALWARE FIX",
  year: "2024",
  country: "GLOBAL",
  runtime: "9 MIN",
  director: "VPNGENIX",
  commentary: "Essential steps to remove threats and secure your hardware.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/malware_fix.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/malware_fix.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/malware_fix.png-wvc-sizes",
  nextScreening: "UPDATED — MAR 19",
  featured: false,
  category: "SECURITY",
  link: "/MalwarebytesPage"
}];
type Film = (typeof fallbackFilms)[number];
function FilmCard({
  film,
  variant,
  index
}: {
  film: Film;
  variant: "featured" | "vertical" | "wide" | "standard";
  index: number;
}) {
  const sizeClasses = {
    featured: "min-h-[560px]",
    vertical: "min-h-[560px]",
    wide: "min-h-[480px]",
    standard: "min-h-[440px]"
  };
  const titleSize = {
    featured: "text-4xl md:text-5xl",
    vertical: "text-2xl md:text-3xl",
    wide: "text-3xl md:text-4xl",
    standard: "text-2xl md:text-3xl"
  };
  return <div data-index={index} className="group relative flex flex-col bg-card/40 backdrop-blur-md border border-border shadow-md transition-all duration-500 hover:-translate-y-1.5 hover:shadow-lg">
      <div className={`relative w-full overflow-hidden ${sizeClasses[variant]}`}>
        <div className="absolute inset-0 h-[65%]">
          <img src={film.image} data-wvc-srcset={film.srcset} data-wvc-sizes={film.sizes} alt={film.title} loading="lazy" decoding="async" className="h-full w-full object-cover grayscale contrast-125 transition-all duration-700 group-hover:grayscale-0 group-hover:contrast-100" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background/80" />
          <div className="absolute inset-0 opacity-0 mix-blend-screen transition-opacity duration-500 group-hover:opacity-30" style={{
          background: "linear-gradient(135deg, hsl(22 100% 50% / 0.4) 0%, transparent 70%)"
        }} />
        </div>

        {variant === "featured" && <div className="absolute top-6 left-6 z-10 border border-primary px-3 py-1.5">
            <span className="font-mono text-xs uppercase tracking-widest text-primary">
              Latest — {film.nextScreening}
            </span>
          </div>}

        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-4">
            {film.year} — {film.country} — {film.runtime}
          </div>
          <h3 className={`font-default font-bold uppercase leading-none tracking-tight text-foreground ${titleSize[variant]} mb-3`}>
            {film.title}
          </h3>
          <p className="font-serif italic text-base md:text-lg text-muted-foreground mb-3">
            by {film.director}
          </p>
          <p className="font-serif text-sm md:text-base text-foreground/80 leading-relaxed max-w-xl">
            {film.commentary}
          </p>

          {variant === "featured" && <div className="mt-6">
              <Link to={film.link || "#"}>
                <Button variant="outline" className="border-primary text-primary bg-transparent hover:bg-primary hover:text-primary-foreground font-mono uppercase tracking-widest text-xs px-6 py-5 shadow-sm">
                  Read Guide
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>}
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-px bg-primary scale-x-0 origin-left transition-transform duration-500 group-hover:scale-x-100" />
      </div>
    </div>;
}
function FilmsGrid() {
  const {
    posts,
    loading
  } = usePosts();
  const [activeFilter, setActiveFilter] = useState("all");
  const films: Film[] = !loading && posts && posts.length >= 6 ? posts.slice(0, 6).map((post, idx) => ({
    id: String(post.id),
    title: post.title || fallbackFilms[idx].title,
    year: fallbackFilms[idx].year,
    country: fallbackFilms[idx].country,
    runtime: fallbackFilms[idx].runtime,
    director: post.author?.name || fallbackFilms[idx].director,
    commentary: post.excerpt || fallbackFilms[idx].commentary,
    image: post.featuredImage || fallbackFilms[idx].image,
    srcset: fallbackFilms[idx].srcset,
    sizes: fallbackFilms[idx].sizes,
    nextScreening: fallbackFilms[idx].nextScreening,
    featured: idx === 0,
    category: fallbackFilms[idx].category,
    link: post.link || "#"
  })) : fallbackFilms;
  return <>
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10 mb-16 md:mb-20">
        <div className="flex-1">
          <div className="font-mono text-sm uppercase tracking-widest text-primary mb-6">
            02 / Insights
          </div>
          <h2 className="font-default font-bold uppercase leading-[0.9] tracking-tight text-foreground" style={{
          fontSize: "clamp(60px, 9vw, 140px)"
        }}>
            TECH
            <br />
            <span className="inline-block" style={{
            WebkitTextStroke: "1.5px hsl(0 0% 88%)",
            color: "transparent"
          }}>
              UPDATES.
            </span>
          </h2>
        </div>

        <div className="flex flex-wrap gap-2 lg:justify-end">
          {filters.map((filter, i) => <Button key={filter.id} data-index={i} onClick={() => setActiveFilter(filter.id)} variant={activeFilter === filter.id ? "default" : "outline"} className={`font-mono text-xs uppercase tracking-widest px-4 py-2.5 transition-all duration-300 ${activeFilter === filter.id ? "border-primary text-primary-foreground bg-primary" : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"}`}>
              {filter.label}
            </Button>)}
        </div>
      </div>

      <div className="space-y-6 md:space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2">
            <FilmCard film={films[0]} variant="featured" index={0} />
          </div>
          <div className="lg:col-span-1">
            <FilmCard film={films[1]} variant="vertical" index={1} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-1">
            <FilmCard film={films[2]} variant="vertical" index={2} />
          </div>
          <div className="lg:col-span-2">
            <FilmCard film={films[3]} variant="wide" index={3} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          <FilmCard film={films[4]} variant="standard" index={4} />
          <FilmCard film={films[5]} variant="standard" index={5} />
          <FilmCard film={films[0]} variant="standard" index={6} />
        </div>
      </div>

      <div className="flex justify-center mt-20 md:mt-24">
        <Link to="/BlogsPage" className="group inline-flex flex-col items-center gap-2">
          <span className="font-mono text-xs md:text-sm uppercase tracking-[0.25em] text-foreground">
            View All Articles — 42 Posts
          </span>
          <span className="block h-px w-full bg-primary transition-all duration-500 group-hover:w-[120%]" />
        </Link>
      </div>
    </>;
}
export default function Relatedgamingarticles() {
  const wp_query_params = {
    post_type: "post",
    per_page: 6,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="4134" id="relatedgamingarticles" className="relative bg-background text-foreground py-40 px-6 md:px-10 overflow-hidden">
      <div className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} />

      <div className="relative mx-auto max-w-[1600px]">
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <FilmsGrid />
        </PostsProvider>
      </div>
    </section>;
}